import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { itemsAPI } from '../../../services/items';
import { lotsAPI } from '../../../services/lots';
import { stockAPI } from '../../../services/stock';
import { productGroupsAPI } from '../../../services/productGroups';
import { msdsAPI } from '../../../services/msds';
import { stockLocationsAPI } from '../../../services/stockLocations';
import { parseGs1, isExpired } from '../utils';

const EMPTY_CREATE = { product_group_id: '', sku: '', name: '', uom: '', description: '' };
const RECENT_LIMIT = 3;

// Lots a scan can book against, soonest expiry first (first expired, first out)
function usableLots(lots) {
  return lots
    .filter(l => l.status === 'active')
    .sort((a, b) => (a.expiry_date || '9999').localeCompare(b.expiry_date || '9999'));
}

export function useScannerState() {
  const { t } = useTranslation(['inventory', 'common']);
  const inputRef = useRef(null);
  const productGroupsRef = useRef([]);
  const busyRef = useRef(false);

  const [phase, setPhase] = useState('scanning');
  const [inputValue, setInputValue] = useState('');
  const [gs1Data, setGs1Data] = useState(null);
  const [item, setItem] = useState(null);
  const [lots, setLots] = useState([]);
  const [matchedLot, setMatchedLot] = useState(null);
  const [stockLevels, setStockLevels] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [scannedBarcode, setScannedBarcode] = useState('');

  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');

  const [msdsOpen, setMsdsOpen] = useState(false);
  const [msds, setMsds] = useState(null);
  const [msdsLoading, setMsdsLoading] = useState(false);

  const [allLocations, setAllLocations] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [createValues, setCreateValues] = useState(EMPTY_CREATE);
  const [createErrors, setCreateErrors] = useState({});
  const [createError, setCreateError] = useState('');

  // Last few bookings this session, newest first, and the undo toast
  const [recent, setRecent] = useState([]);
  const [toast, setToast] = useState(null);

  const loadProductGroups = useCallback(async () => {
    const res = await productGroupsAPI.getProductGroups();
    const groups = res.data?.data ?? [];
    setProductGroups(groups);
    productGroupsRef.current = groups;
    return groups;
  }, []);

  useEffect(() => {
    loadProductGroups().catch(() => {});
    stockLocationsAPI.getStockLocations()
      .then(res => setAllLocations((res.data?.data ?? []).filter(l => l.active)))
      .catch(() => {});
  }, [loadProductGroups]);

  useEffect(() => {
    if (phase === 'scanning') inputRef.current?.focus();
  }, [phase]);

  const errorText = useCallback(
    (err) => err?.response?.data?.message || err?.message || t('inventory:scanner.error_generic'),
    [t],
  );

  const clearItem = () => {
    setGs1Data(null);
    setItem(null);
    setLots([]);
    setMatchedLot(null);
    setStockLevels([]);
    setErrorMessage('');
    setScannedBarcode('');
    setActionError('');
    setMsdsOpen(false);
    setMsds(null);
    setCreateValues(EMPTY_CREATE);
    setCreateErrors({});
    setCreateError('');
  };

  const resetScanner = useCallback(() => {
    clearItem();
    setInputValue('');
    setPhase('scanning');
  }, []);

  // Load stock (and lots, for lot-tracked items) and pick the phase to show
  const showItem = useCallback(async (foundItem, parsed) => {
    let groups = productGroupsRef.current;
    if (groups.length === 0) groups = await loadProductGroups().catch(() => []);
    const lotTracked = foundItem.product_group?.lot_tracked
      ?? groups.find(g => g.product_group_id === foundItem.product_group_id)?.lot_tracked
      ?? false;

    const [stockRes, lotsRes] = await Promise.all([
      stockAPI.getStockLevelByLocation(foundItem.item_id),
      lotTracked ? lotsAPI.getByItem(foundItem.item_id) : Promise.resolve(null),
    ]);
    setItem(foundItem);
    setStockLevels(stockRes.data?.data?.location_balances ?? []);

    const itemLots = lotsRes?.data?.data ?? [];
    setLots(itemLots);

    if (lotTracked && parsed?.lot) {
      const match = itemLots.find(l => l.lot_number.toLowerCase() === parsed.lot.toLowerCase());
      setMatchedLot(match ?? null);
      if (!match) {
        setPhase('add_lot');
        return;
      }
    }
    setPhase('result');
  }, [loadProductGroups]);

  const handleBarcode = useCallback(async (raw) => {
    const trimmed = raw.trim();
    if (!trimmed || busyRef.current) return;
    busyRef.current = true;

    clearItem();
    setToast(null);
    const parsed = parseGs1(trimmed);
    setGs1Data(parsed);
    setPhase('loading');

    const lookupBarcode = parsed?.gtin ?? [...trimmed].filter(c => c.charCodeAt(0) >= 32).join('');

    try {
      const itemRes = await itemsAPI.scanByBarcode(lookupBarcode);
      await showItem(itemRes.data?.data, parsed);
    } catch (err) {
      if (err?.response?.status === 404) {
        setScannedBarcode(lookupBarcode);
        setCreateValues({ ...EMPTY_CREATE, sku: lookupBarcode });
        setPhase('not_found');
      } else {
        setErrorMessage(t('inventory:scanner.error_generic'));
        setPhase('error');
      }
    } finally {
      busyRef.current = false;
    }
  }, [showItem, t]);

  const itemProductGroup = productGroups.find(g => g.product_group_id === item?.product_group_id);
  const itemLotTracked = item?.product_group?.lot_tracked ?? itemProductGroup?.lot_tracked ?? false;
  const itemHazardous = item?.product_group?.hazardous ?? itemProductGroup?.hazardous ?? false;

  /**
   * Book a use (-qty) or an add (+qty), then go straight back to scanning
   * with a confirmation toast (undo is on the recent list). For a lot-tracked
   * item without a scanned lot, a use takes from the oldest lot first (the
   * server splits across lots).
   */
  const book = useCallback(async ({ direction, qty, locationId = null, lotId = null, locationName = null }) => {
    if (!item || !qty) return;
    setSaving(true);
    setActionError('');
    try {
      const res = await stockAPI.recordTransaction({
        item_id: item.item_id,
        lot_id: matchedLot?.lot_id ?? lotId ?? null,
        quantity_change: direction === 'use' ? -Math.abs(qty) : Math.abs(qty),
        reason: direction === 'use' ? 'dispensed' : 'received',
        location_id: locationId,
      });
      const entry = {
        id: `${Date.now()}`,
        direction,
        qty,
        uom: item.uom ?? '',
        itemName: item.name,
        locationName,
        txIds: (res.data?.data ?? []).map(tx => tx.stock_transaction_id),
        undone: false,
      };
      setRecent(prev => [entry, ...prev].slice(0, RECENT_LIMIT));
      setToast({ entry });
      resetScanner();
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setSaving(false);
    }
  }, [item, matchedLot, resetScanner, errorText]);

  // Undo books the opposite transaction(s); nothing is deleted
  const undo = useCallback(async (entry) => {
    setToast(null);
    try {
      for (const txId of entry.txIds) {
        // Sequential: each reversal updates the running balance the next one reads
        // eslint-disable-next-line no-await-in-loop
        await stockAPI.reverseTransaction(txId);
      }
      setRecent(prev => prev.map(e => (e.id === entry.id ? { ...e, undone: true } : e)));
      setToast({ message: t('inventory:scanner.undone') });
      inputRef.current?.focus();
    } catch (err) {
      setToast({ message: errorText(err), severity: 'error' });
    }
  }, [t, errorText]);

  const handleAddLot = async (qty, locationId = null) => {
    if (!gs1Data?.lot || !item || !qty) return;
    setSaving(true);
    setActionError('');
    try {
      const res = await lotsAPI.create({
        item_id: item.item_id,
        lot_number: gs1Data.lot,
        initial_quantity: Number(qty),
        expiry_date: gs1Data.expiry ?? null,
        location_id: locationId,
      });
      const lot = res.data?.data ?? null;
      setMatchedLot(lot);
      setLots(prev => (lot ? [...prev, lot] : prev));
      const stockRes = await stockAPI.getStockLevelByLocation(item.item_id);
      setStockLevels(stockRes.data?.data?.location_balances ?? []);
      setPhase('result');
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMsds = useCallback(async () => {
    if (!item) return;
    setMsdsOpen(true);
    if (msds !== null) return;
    setMsdsLoading(true);
    try {
      const res = await msdsAPI.getMSDS(item.item_id);
      setMsds(res.data?.data ?? null);
    } catch {
      setMsds(null);
    } finally {
      setMsdsLoading(false);
    }
  }, [item, msds]);

  const startCreateItem = useCallback(() => setPhase('create_item'), []);

  const handleCreateValueChange = useCallback((field, value) => {
    setCreateValues(prev => ({ ...prev, [field]: value }));
    setCreateErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const handleCreateItem = useCallback(async () => {
    setCreateError('');
    const errors = {};
    if (!createValues.product_group_id) errors.product_group_id = t('inventory:items.validation.product_group_required');
    if (!createValues.sku.trim()) errors.sku = t('inventory:items.validation.sku_required');
    if (!createValues.name.trim()) errors.name = t('inventory:items.validation.name_required');
    if (!createValues.uom.trim()) errors.uom = t('inventory:items.validation.uom_required');
    if (Object.keys(errors).length > 0) { setCreateErrors(errors); return; }

    setSaving(true);
    try {
      const res = await itemsAPI.createItem({
        product_group_id: createValues.product_group_id,
        sku: createValues.sku.trim(),
        name: createValues.name.trim(),
        description: createValues.description.trim() || null,
        uom: createValues.uom.trim(),
      });
      await showItem(res.data?.data, gs1Data);
    } catch (err) {
      setCreateError(errorText(err));
    } finally {
      setSaving(false);
    }
  }, [createValues, gs1Data, showItem, t, errorText]);

  const backToResult = useCallback(() => { setActionError(''); setPhase('result'); }, []);
  const goToUseStock = useCallback(() => { setActionError(''); setPhase('use_stock'); }, []);
  const goToAddStock = useCallback(() => { setActionError(''); setPhase('add_stock'); }, []);

  const totalStock = stockLevels.reduce((sum, loc) => sum + (loc.quantity ?? 0), 0);
  const expiryDate = gs1Data?.expiry ?? matchedLot?.expiry_date ?? null;
  const expired =
    matchedLot?.status === 'expired' || matchedLot?.status === 'recalled'
      ? true
      : isExpired(expiryDate);

  return {
    inputRef,
    phase,
    inputValue,
    setInputValue,
    gs1Data,
    item,
    lots: usableLots(lots),
    matchedLot,
    stockLevels,
    allLocations,
    errorMessage,
    scannedBarcode,
    expiryDate,
    expired,
    saving,
    actionError,
    totalStock,
    itemLotTracked,
    itemHazardous,
    msdsOpen,
    setMsdsOpen,
    msds,
    msdsLoading,
    handleOpenMsds,
    handleBarcode,
    handleAddLot,
    book,
    undo,
    recent,
    toast,
    closeToast: () => setToast(null),
    resetScanner,
    backToResult,
    goToUseStock,
    goToAddStock,
    productGroups,
    createValues,
    createErrors,
    createError,
    startCreateItem,
    handleCreateValueChange,
    handleCreateItem,
  };
}
